import {type FormatType} from "@/class/Clock.ts";
import {CalendarDaysIcon} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {useTimer} from "@/hooks/useTimer.ts";
import {useFormat} from "@/hooks/useFormat.tsx";
import {clsx} from "clsx";

interface TimerProps {
    fullScreen?: boolean // Si true : afficher en plein écran (mode simplifié)
}

/**
 * Affiche l'heure principale (heures, minutes, secondes), la date et les boutons
 * de changement de format (12h / 24h). Supporte un mode plein écran.
 * Timer pour la page principale.
 * - Utilise useTimer pour obtenir l'heure, la date et la locale (isEn).
 * - Utilise useFormat pour changer entre 12h et 24h.
 * - En mode plein écran, affiche seulement le timer et un bouton pour quitter.
 */
export function Timer({fullScreen}: TimerProps) {
    const {changeFormat} = useFormat()
    const {isEn, timer, date, format} = useTimer()

    // Déclenche un CustomEvent pour informer l'application de la sortie du mode plein écran
    const exitFullScreen = () => {
        const exitFullScreenEvent = new CustomEvent("extfullscreen")
        window.dispatchEvent(exitFullScreenEvent)
    }

    const timerClass = {
        duration: "font-bold text-[350px] max-[1500px]:text-[250px] max-[1075px]:text-[180px] max-[825px]:text-[100px] max-[490px]:text-[75px]",
        separator: "text-[220px] font-bold mx-2 relative -top-12 animate max-[1500px]:text-[120px] max-[1075px]:text-[90px] max-[1075px]:-top-8 max-[825px]:text-[40px] max-[825px]:-top-4",
        suffix: "font-bold text-[80px] max-[1095px]:text-[50px] max-[490px]:text-[25px]"
    }

    return <div
        className={clsx(
            !fullScreen
                ? "border-b max-[1500px]:w-[85%] mx-[30px]"
                : "h-full w-full z-[99999] bg-[#eee] dark:bg-[#000] fixed top-0 left-0 flex items-center justify-center"
        )}
        onClick={exitFullScreen}
    >
        {/* Affichage principal centré du timer */}
        <div className="flex items-center justify-center">
            <p className="max-[825px]:my-10">
                <span className={timerClass.duration}>{timer.hours}</span>
                <span className={timerClass.separator}>:</span>
                <span className={timerClass.duration}>{timer.minutes}</span>
                <span className={timerClass.separator}>:</span>
                <span className={timerClass.duration}>{timer.seconds}</span>
                {/* Suffixe AM/PM uniquement si la locale est anglaise */}
                {isEn && <span className={timerClass.suffix}>{timer.suffix}</span>}
            </p>
        </div>

        {/* Vue standard avec date et options de format */}
        {!fullScreen && (
            <div className="flex justify-between items-center gap-3 relative -top-10 max-[490px]:flex-col">
                <p className="text-primary max-[825px]:hidden">{isEn ? "Current Date" : "Date"}</p>

                <div className="text-[18px] font-bold">
                    <p className="flex gap-1">
                        <span className="max-[490px]:hidden">{isEn ? "Today" : "Aujourd'hui"}</span> <CalendarDaysIcon/> : {date}
                    </p>
                </div>

                <div className="bg-white dark:bg-card rounded-4xl p-1 flex gap-1 border">
                    {[
                        {name: "12h", format: "en-EN"},
                        {name: "24h", format: "fr-FR"}
                    ].map(b => (
                        <Button
                            className="cursor-pointer text-[17px] max-[490px]:text-[14px]"
                            variant={format !== b.format ? "ghost" : "default"}
                            onClick={() => changeFormat((b.format as FormatType))}
                        >
                            {b.name}
                        </Button>
                    ))}
                </div>
            </div>
        )}
    </div>

}